import { useState, useCallback } from "react";
import { cloudinaryService } from "@/services/cloudinary";
import { s3Service } from "@/services/s3-service";
import { useToast } from "@/components/ui/use-toast";

export interface PhotoUploadError {
  photoIndex: number;
  photoPreview: string;
  error: string;
  fileName?: string;
}

export interface PhotoUploadResult {
  success: boolean;
  uploadedUrls: string[];
  errors: PhotoUploadError[];
}

export const usePhotoUploadValidation = () => {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    currentPhoto: 0,
    totalPhotos: 0,
    uploadingText: "",
  });

  const validatePhotoForUpload = useCallback(
    (photo: any): { valid: boolean; error?: string } => {
      if (!photo.file || !(photo.file instanceof File)) {
        if (photo.url) return { valid: true }; // Already uploaded
        return { valid: false, error: "Arquivo de imagem inválido" };
      }

      // Check file size (max 10MB)
      if (photo.file.size > 10 * 1024 * 1024) {
        return { valid: false, error: "Arquivo muito grande (máximo 10MB)" };
      }

      // Check file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(photo.file.type)) {
        return {
          valid: false,
          error: "Tipo de arquivo inválido (use JPEG, PNG ou WEBP)",
        };
      }

      return { valid: true };
    },
    [],
  );

  const uploadPhotos = useCallback(
    async (photos: any[]): Promise<PhotoUploadResult> => {
      const result: PhotoUploadResult = {
        success: true,
        uploadedUrls: [],
        errors: [],
      };

      if (!photos || photos.length === 0) {
        return result;
      }

      setIsUploading(true);
      setUploadProgress({
        currentPhoto: 0,
        totalPhotos: photos.length,
        uploadingText: "Validando fotos...",
      });

      toast({
        title: "Upload iniciado",
        description: `Processando ${photos.length} foto${photos.length > 1 ? "s" : ""}...`,
      });

      // Validate all photos first
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        const validation = validatePhotoForUpload(photo);

        if (!validation.valid) {
          result.success = false;
          result.errors.push({
            photoIndex: i,
            photoPreview: photo.preview || "",
            error: validation.error || "Erro de validação",
            fileName: photo.file?.name,
          });
          result.uploadedUrls.push("");
          continue;
        }
      }

      // If validation failed, return early
      if (!result.success) {
        setIsUploading(false);
        return result;
      }

      // Upload each photo
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];

        setUploadProgress((prev) => ({
          ...prev,
          currentPhoto: i + 1,
          uploadingText: `Fazendo upload da foto ${i + 1} de ${photos.length}...`,
        }));

        let hasValidCloudinaryUrl = false;

        try {
          // Check if already uploaded
          if (photo.url && !photo.file) {
            result.uploadedUrls.push(photo.url);
            continue;
          }

          if (photo.file && photo.file instanceof File) {
            // Upload to Cloudinary first
            const cloudinaryUrl = await cloudinaryService.upload(
              photo.file,
              "privaterooms",
            );

            // Validate Cloudinary response
            if (!cloudinaryUrl) {
              throw new Error("Upload para Cloudinary retornou URL inválida");
            }

            hasValidCloudinaryUrl = true;

            // Update progress for S3 upload
            setUploadProgress((prev) => ({
              ...prev,
              uploadingText: `Finalizando upload da foto ${i + 1} de ${photos.length}...`,
            }));

            // Then upload to S3
            const s3Url = await s3Service.uploadToS3(
              cloudinaryUrl,
              "privaterooms",
            );

            if (!s3Url) {
              throw new Error("Upload para S3 retornou URL inválida");
            }

            result.uploadedUrls.push(s3Url);
          } else {
            throw new Error("Dados da foto são inválidos");
          }
        } catch (error) {
          console.error(`Error uploading photo ${i + 1}:`, error);
          result.success = false;

          let errorMessage = "Erro desconhecido";
          if (error instanceof Error) {
            if (hasValidCloudinaryUrl) {
              errorMessage =
                "Falha ao salvar imagem no armazenamento. Tente novamente.";
            } else if (error.message.includes("minimum pixel resolution")) {
              errorMessage = "Imagem muito pequena (mínimo 80x80 pixels)";
            } else if (error.message.includes("Content is not available")) {
              errorMessage = "Falha no processamento da imagem";
            } else if (error.message.includes("400")) {
              errorMessage = "Formato de imagem inválido";
            } else {
              errorMessage = error.message;
            }
          }

          result.errors.push({
            photoIndex: i,
            photoPreview: photo.preview || "",
            error: errorMessage,
            fileName: photo.file?.name,
          });
          result.uploadedUrls.push("");
        }
      }

      setIsUploading(false);
      setUploadProgress({
        currentPhoto: 0,
        totalPhotos: 0,
        uploadingText: "",
      });

      if (result.success) {
        toast({
          title: "Upload concluído",
          description: `${photos.length} foto${photos.length > 1 ? "s foram" : " foi"} enviada${photos.length > 1 ? "s" : ""} com sucesso!`,
          variant: "default",
        });
      } else {
        toast({
          title: "Erro no upload",
          description: `${result.errors.length} foto${result.errors.length > 1 ? "s apresentaram" : " apresentou"} problema${result.errors.length > 1 ? "s" : ""}. Verifique os detalhes.`,
          variant: "destructive",
        });
      }

      return result;
    },
    [validatePhotoForUpload],
  );

  const retryPhotoUpload = useCallback(
    async (photos: any[], photoIndex: number): Promise<PhotoUploadResult> => {
      return uploadPhotos([photos[photoIndex]]);
    },
    [uploadPhotos],
  );

  return {
    isUploading,
    uploadProgress,
    uploadPhotos,
    retryPhotoUpload,
    validatePhotoForUpload,
  };
};
